import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormHelperText,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  alpha,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';

// واجهة بيانات الحساب
interface AccountData {
  id?: string;
  accountNumber: string;
  name: string;
  type: 'رئيسي' | 'فرعي';
  accountCategory: 'أصول' | 'خصوم' | 'حقوق ملكية' | 'إيرادات' | 'مصروفات';
  parentAccount: string | null;
  initialBalance: number;
  isActive: boolean;
  isGeneralAccount: boolean;
  createdBy?: string;  // معرّف المستخدم الذي أنشأ الحساب
}

// نموذج حساب فارغ للإنشاء
const emptyAccount: AccountData = {
  accountNumber: '',
  name: '',
  type: 'رئيسي',
  accountCategory: 'أصول',
  parentAccount: null,
  initialBalance: 0,
  isActive: true,
  isGeneralAccount: false
};

// واجهة حسابات القائمة المنسدلة
interface ParentAccountOption {
  id: string;
  name: string;
  accountNumber: string;
  type: string;
}

const AccountForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEditMode = Boolean(id);
  
  // حالة النموذج
  const [formData, setFormData] = useState<AccountData>(emptyAccount);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [parentAccountOptions, setParentAccountOptions] = useState<ParentAccountOption[]>([]);
  
  // جلب الحسابات النشطة للقائمة المنسدلة
  useEffect(() => {
    const fetchParentAccounts = async () => {
      try {
        // استدعاء API لجلب الحسابات النشطة
        const accounts = await apiService.get<ParentAccountOption[]>('/finance/accounts/selection/active');
        setParentAccountOptions(accounts);
      } catch (err: any) {
        console.error('خطأ في جلب الحسابات:', err);
      }
    };
    
    fetchParentAccounts();
  }, []);
  
  // جلب بيانات الحساب عند التعديل
  useEffect(() => {
    const fetchAccountData = async () => {
      if (isEditMode && id) {
        setLoading(true);
        try {
          // استدعاء API لجلب بيانات الحساب
          const accountData = await apiService.get<AccountData>(`/finance/accounts/${id}`);
          setFormData(accountData);
        } catch (err: any) {
          console.error('خطأ في جلب بيانات الحساب:', err);
          setErrors({
            form: err.response?.data?.message || 'حدث خطأ في جلب بيانات الحساب'
          });
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchAccountData();
  }, [id, isEditMode]);

  // تحديث رقم الحساب تلقائيًا عند اختيار الحساب الرئيسي
  useEffect(() => {
    const generateSequentialAccountNumber = async () => {
      if (formData.type === 'فرعي' && formData.parentAccount) {
        const parent = parentAccountOptions.find(p => p.id === formData.parentAccount);
        if (parent && !isEditMode) {
          try {
            console.log(`جلب الحسابات الفرعية للحساب الرئيسي: ${formData.parentAccount}`);
            
            // استخدام المسار الصحيح للحصول على الحسابات الفرعية باستخدام معلمة parentAccount
            const response = await apiService.get<AccountData[]>(`/finance/accounts?parentAccount=${formData.parentAccount}&type=فرعي`);
            
            console.log(`تم استلام ${response.length} حساب فرعي`);
            
            let maxSequence = 0;
            const parentNumber = parent.accountNumber;
            
            // البحث عن أعلى رقم تسلسلي للحسابات الفرعية الحالية
            if (Array.isArray(response) && response.length > 0) {
              // استخراج الرقم التسلسلي من كل حساب فرعي
              response.forEach(account => {
                if (account.accountNumber.startsWith(parentNumber)) {
                  // استخراج الرقم التسلسلي (الأرقام بعد رقم الحساب الرئيسي)
                  const sequencePart = account.accountNumber.substring(parentNumber.length);
                  const sequenceNumber = parseInt(sequencePart, 10);
                  
                  if (!isNaN(sequenceNumber) && sequenceNumber > maxSequence) {
                    maxSequence = sequenceNumber;
                  }
                }
              });
            }
            
            // زيادة الرقم التسلسلي بواحد للحساب الفرعي الجديد
            const nextSequence = maxSequence + 1;
            
            // تنسيق الرقم التسلسلي بإضافة أصفار قبله إذا كان أقل من 10
            const formattedSequence = nextSequence.toString().padStart(2, '0');
            
            // إنشاء رقم الحساب الفرعي الجديد
            const suggestedNumber = `${parentNumber}${formattedSequence}`;
            
            console.log(`توليد رقم حساب فرعي تسلسلي: ${suggestedNumber} (الأب: ${parentNumber}, التسلسل: ${formattedSequence}, ناتج من أعلى تسلسل: ${maxSequence})`);
            
            setFormData(prev => ({ ...prev, accountNumber: suggestedNumber }));
          } catch (error: any) {
            console.error('خطأ في جلب الحسابات الفرعية:', error);
            
            // طباعة تفاصيل الخطأ للمساعدة في تشخيص المشكلة
            console.error('تفاصيل الخطأ:', error.response?.data || error.message || 'خطأ غير معروف');
            
            // في حالة فشل جلب البيانات، نحاول استخدام أي حسابات فرعية موجودة في القائمة المنسدلة
            let maxSequenceFromDropdown = 0;
            
            // البحث في قائمة الخيارات المنسدلة عن أي حسابات فرعية لنفس الحساب الرئيسي
            parentAccountOptions.forEach(account => {
              if (account.type === 'فرعي' && account.accountNumber.startsWith(parent.accountNumber)) {
                const sequencePart = account.accountNumber.substring(parent.accountNumber.length);
                const sequenceNumber = parseInt(sequencePart, 10);
                
                if (!isNaN(sequenceNumber) && sequenceNumber > maxSequenceFromDropdown) {
                  maxSequenceFromDropdown = sequenceNumber;
                }
              }
            });
            
            // إذا وجدنا حسابات فرعية في القائمة المنسدلة، نستخدم أعلى رقم + 1
            // وإلا نستخدم الرقم الافتراضي 01
            if (maxSequenceFromDropdown > 0) {
              const nextSequence = maxSequenceFromDropdown + 1;
              const formattedSequence = nextSequence.toString().padStart(2, '0');
              const suggestedNumber = `${parent.accountNumber}${formattedSequence}`;
              
              console.log(`توليد رقم من القائمة المنسدلة: ${suggestedNumber} (التسلسل: ${formattedSequence})`);
              setFormData(prev => ({ ...prev, accountNumber: suggestedNumber }));
            } else {
              // استخدام الرقم الافتراضي 01 إذا لم نجد أي حسابات فرعية
              const suggestedNumber = `${parent.accountNumber}01`;
              console.log(`استخدام الرقم الافتراضي: ${suggestedNumber}`);
              setFormData(prev => ({ ...prev, accountNumber: suggestedNumber }));
            }
          }
        }
      }
    };
    
    if (formData.type === 'فرعي' && formData.parentAccount) {
      generateSequentialAccountNumber();
    }
  }, [formData.type, formData.parentAccount, isEditMode, parentAccountOptions]);

  // معالجة تغيير حقول النص والأرقام
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // تعامل خاص مع الأرقام
    if (name === "initialBalance") {
      const numValue = value === '' ? 0 : Number(value);
      console.log(`Setting ${name} to:`, numValue);
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      console.log(`Setting ${name} to:`, value);
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // مسح رسالة الخطأ عند التغيير
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير حقول Select
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    
    if (name) {
      console.log(`Select change - ${name}:`, value);
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // مسح رسالة الخطأ عند التغيير
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // معالجة تغيير Switch
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    console.log('Setting isActive to:', isChecked);
    setFormData(prev => ({ ...prev, isActive: isChecked }));
  };

  // معالجة تغيير نوع الحساب
  const handleTypeChange = (e: SelectChangeEvent<'رئيسي' | 'فرعي'>) => {
    const value = e.target.value as 'رئيسي' | 'فرعي';
    console.log('Changing account type to:', value);
    setFormData(prev => {
      const newData = {
        ...prev,
        type: value
      };
      console.log('Updated form data after type change:', newData);
      return newData;
    });
  };

  // التحقق من صحة البيانات
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // تحقق من رقم الحساب
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'رقم الحساب مطلوب';
    }
    
    // تحقق من اسم الحساب
    if (!formData.name.trim()) {
      newErrors.name = 'اسم الحساب مطلوب';
    }
    
    // تحقق من تصنيف الحساب
    if (!formData.accountCategory) {
      newErrors.accountCategory = 'تصنيف الحساب مطلوب';
    }
    
    // تحقق من الحساب الرئيسي للحسابات الفرعية فقط
    if (formData.type === 'فرعي' && !formData.parentAccount) {
      newErrors.parentAccount = 'يجب اختيار حساب رئيسي للحسابات الفرعية';
    }
    
    // تحقق من الرصيد الافتتاحي
    const initialBalance = Number(formData.initialBalance);
    if (isNaN(initialBalance)) {
      newErrors.initialBalance = 'الرصيد الافتتاحي يجب أن يكون رقم';
    }
    
    console.log('Form validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // معالجة حفظ النموذج
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaveLoading(true);
    
    // إرسال البيانات إلى API
    try {
      // تحويل الرصيد الافتتاحي إلى رقم صريح
      const dataToSend = {
        ...formData,
        initialBalance: Number(formData.initialBalance)
      };
      
      // إضافة معرف المستخدم إذا كان موجوداً في localStorage
      const userToken = localStorage.getItem('token');
      if (userToken) {
        try {
          // محاولة استخراج بيانات المستخدم من الرمز إذا كان متاحاً
          const tokenData = JSON.parse(atob(userToken.split('.')[1]));
          if (tokenData && tokenData.id) {
            dataToSend.createdBy = tokenData.id;
          }
        } catch (tokenError) {
          console.error('خطأ في استخراج بيانات المستخدم من الرمز:', tokenError);
        }
      }
      
      console.log('Sending data to API:', dataToSend);
      
      let response;
      if (isEditMode) {
        response = await apiService.put<AccountData>(`/finance/accounts/${id}`, dataToSend);
        console.log('Account update response:', response);
      } else {
        response = await apiService.post<AccountData>('/finance/accounts', dataToSend);
        console.log('Account create response:', response);
      }
      
      setSaveLoading(false);
      setSuccess(true);
      
      // العودة إلى صفحة القائمة بعد النجاح
      setTimeout(() => {
        navigate('/finance/accounts');
      }, 1500);
    } catch (err: any) {
      setSaveLoading(false);
      
      // تسجيل تفاصيل الخطأ للتصحيح
      console.error('خطأ في حفظ الحساب:', err);
      
      // عرض رسالة خطأ مفصلة استناداً إلى نوع الخطأ
      if (err.response) {
        console.log('تفاصيل استجابة الخطأ:', err.response.data);
        
        const status = err.response.status;
        const errorMessage = err.response.data?.message || 'حدث خطأ أثناء حفظ البيانات';
        
        if (status === 400) {
          // خطأ التحقق من صحة البيانات
          if (errorMessage.includes('مستخدم بالفعل')) {
            setErrors(prev => ({
              ...prev,
              accountNumber: 'رقم الحساب مستخدم بالفعل',
              form: errorMessage
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              form: errorMessage
            }));
          }
        } else if (status === 401) {
          // خطأ المصادقة
          setErrors(prev => ({
            ...prev,
            form: 'جلسة المستخدم منتهية. الرجاء تسجيل الدخول مرة أخرى.'
          }));
          
          // يمكن إعادة توجيه المستخدم إلى صفحة تسجيل الدخول بعد فترة
          // setTimeout(() => navigate('/login'), 2000);
        } else {
          // أي خطأ آخر من الخادم
          if (err.response.data && err.response.data.error) {
            // محاولة تحديد الأخطاء المتعلقة بمعرّف المنشئ
            const errorString = String(err.response.data.error);
            if (errorString.includes('createdBy') || errorString.includes('user')) {
              setErrors(prev => ({
                ...prev,
                form: 'خطأ في معلومات المستخدم. يرجى تسجيل الدخول مرة أخرى.'
              }));
              return;
            }
          }
          
          setErrors(prev => ({
            ...prev,
            form: `${errorMessage} (${status})`
          }));
        }
      } else if (err.request) {
        // لم يتم تلقي استجابة من الخادم
        setErrors(prev => ({
          ...prev,
          form: 'لم يتم تلقي رد من الخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.'
        }));
      } else {
        // خطأ في إعداد الطلب
        setErrors(prev => ({
          ...prev,
          form: `حدث خطأ قبل إرسال الطلب: ${err.message}`
        }));
      }
    }
  };

  // العنوان بناءً على وضع النموذج
  const title = isEditMode ? 'تعديل الحساب' : 'إضافة حساب جديد';

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        {/* العنوان ومسار التنقل */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceIcon sx={{ mr: 1, color: '#30c3b0' }} />
              {title}
            </Typography>
            
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link to="/finance/accounts" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                الحسابات العامة
              </Link>
              <Typography color="text.primary">{title}</Typography>
            </Breadcrumbs>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/finance/accounts"
            sx={{
              borderColor: '#30c3b0',
              color: '#30c3b0',
              '&:hover': { borderColor: '#25a899', backgroundColor: alpha('#30c3b0', 0.04) },
            }}
          >
            العودة للقائمة
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* عرض التحميل أثناء جلب البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    معلومات الحساب الأساسية
                  </Typography>
                  
                  <TextField
                    name="name"
                    label="اسم الحساب"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    fullWidth
                    margin="normal"
                    required
                  />
                  
                  <TextField
                    name="accountNumber"
                    label="رقم الحساب"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    error={!!errors.accountNumber}
                    helperText={errors.accountNumber || (formData.type === 'فرعي' ? 'سيتم توليد رقم حساب فرعي تسلسلي تلقائيًا عند اختيار الحساب الرئيسي' : '')}
                    fullWidth
                    margin="normal"
                    required
                    disabled={formData.type === 'فرعي'}
                  />
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    نوع الحساب والعلاقات
                  </Typography>
                  
                  <FormControl fullWidth margin="normal" required error={!!errors.accountCategory}>
                    <InputLabel>تصنيف الحساب</InputLabel>
                    <Select
                      name="accountCategory"
                      value={formData.accountCategory}
                      onChange={handleSelectChange}
                      label="تصنيف الحساب"
                    >
                      <MenuItem value="أصول">أصول</MenuItem>
                      <MenuItem value="خصوم">خصوم</MenuItem>
                      <MenuItem value="حقوق ملكية">حقوق ملكية</MenuItem>
                      <MenuItem value="إيرادات">إيرادات</MenuItem>
                      <MenuItem value="مصروفات">مصروفات</MenuItem>
                    </Select>
                    {errors.accountCategory && (
                      <FormHelperText>{errors.accountCategory}</FormHelperText>
                    )}
                    {!errors.accountCategory && (
                      <FormHelperText>
                        التصنيف المحاسبي للحساب حسب المعايير المحاسبية
                      </FormHelperText>
                    )}
                  </FormControl>
                  
                  <FormControl fullWidth margin="normal">
                    <InputLabel>نوع الحساب</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleTypeChange}
                      label="نوع الحساب"
                    >
                      <MenuItem value="رئيسي">حساب رئيسي</MenuItem>
                      <MenuItem value="فرعي">حساب فرعي</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={!!errors.parentAccount}
                  >
                    <InputLabel>{formData.type === 'رئيسي' ? 'الحساب الرئيسي الأعلى' : 'الحساب الرئيسي'}</InputLabel>
                    <Select
                      name="parentAccount"
                      value={formData.parentAccount || ''}
                      onChange={handleSelectChange}
                      label={formData.type === 'رئيسي' ? 'الحساب الرئيسي الأعلى' : 'الحساب الرئيسي'}
                      required={formData.type === 'فرعي'} // إلزامي فقط للحسابات الفرعية
                    >
                      {formData.type === 'رئيسي' && (
                        <MenuItem value="">
                          <em>بدون حساب رئيسي (حساب رئيسي من المستوى الأول)</em>
                        </MenuItem>
                      )}
                      {parentAccountOptions
                        .filter(account => {
                          // منع اختيار نفس الحساب كحساب رئيسي عند التعديل
                          return isEditMode ? account.id !== id : true;
                        })
                        .filter(account => {
                          // في حالة الحساب الرئيسي، نعرض فقط الحسابات الرئيسية للاختيار
                          // وفي حالة الحساب الفرعي، نظهر جميع الحسابات الرئيسية
                          return formData.type === 'رئيسي' ? account.type === 'رئيسي' : account.type === 'رئيسي';
                        })
                        .map(account => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.accountNumber} - {account.name}
                          </MenuItem>
                        ))
                      }
                    </Select>
                    {errors.parentAccount && (
                      <FormHelperText>{errors.parentAccount}</FormHelperText>
                    )}
                    {!errors.parentAccount && formData.type === 'رئيسي' && (
                      <FormHelperText>
                        يمكنك اختيار حساب رئيسي أعلى لإنشاء تسلسل هرمي للحسابات الرئيسية
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Grid>
              
              <Grid item xs={12} lg={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    المعلومات المالية
                  </Typography>
                  
                  <TextField
                    name="initialBalance"
                    label="الرصيد الافتتاحي"
                    value={formData.initialBalance}
                    onChange={handleInputChange}
                    type="number"
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1 }}>ريال</Typography>,
                    }}
                    error={!!errors.initialBalance}
                    helperText={errors.initialBalance}
                    fullWidth
                    margin="normal"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.isGeneralAccount}
                        onChange={(e) => setFormData(prev => ({ ...prev, isGeneralAccount: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label="عرض في الحسابات العامة"
                    sx={{ mt: 1 }}
                  />
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                    الإعدادات
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={handleSwitchChange}
                        color="success"
                      />
                    }
                    label="حساب نشط"
                  />
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    الحسابات غير النشطة لن تظهر في قوائم الاختيار عند إنشاء القيود المحاسبية
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            {/* رسائل الخطأ */}
            {errors.form && (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
                onClose={() => setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.form;
                  return newErrors;
                })}
              >
                {errors.form}
              </Alert>
            )}
            
            {/* رسالة النجاح */}
            {success && (
              <Alert 
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                sx={{ mb: 3 }}
              >
                تم {isEditMode ? 'تعديل' : 'إضافة'} الحساب بنجاح
              </Alert>
            )}
            
            {/* أزرار الإجراءات */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/finance/accounts')}
                disabled={saveLoading}
              >
                إلغاء
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
                disabled={saveLoading || success}
                sx={{
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
                }}
              >
                {saveLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  isEditMode ? 'حفظ التغييرات' : 'إضافة الحساب'
                )}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default AccountForm;